import { useState, useCallback } from "react";
import { loadGoogleMaps } from "../utils/googleMaps";

/** Full address for API / booking; short title stays in `address` for the input field. */
export function getPlaceFullAddress(place) {
  if (!place) return "";
  return String(place.fullAddress || place.address || "").trim();
}

/** Uber-style primary title for the input field. */
export function getPlaceDisplayTitle(placeOrPrediction) {
  if (!placeOrPrediction) return "";

  const main = placeOrPrediction?.structured_formatting?.main_text;
  if (main) return String(main).trim();

  if (placeOrPrediction.title) return String(placeOrPrediction.title).trim();

  const name = placeOrPrediction.name;
  const formatted =
    placeOrPrediction.formatted_address || placeOrPrediction.fullAddress;
  if (name && formatted && String(name).trim() !== String(formatted).trim()) {
    return String(name).trim();
  }

  // Already-short field value (selected place state)
  if (
    placeOrPrediction.address &&
    placeOrPrediction.fullAddress &&
    String(placeOrPrediction.address).trim() !==
      String(placeOrPrediction.fullAddress).trim()
  ) {
    return String(placeOrPrediction.address).trim();
  }

  const full = String(
    placeOrPrediction.fullAddress ||
      placeOrPrediction.formatted_address ||
      placeOrPrediction.description ||
      placeOrPrediction.address ||
      "",
  ).trim();
  if (!full) return "";

  const parts = full
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  // "57, Mohali Bypass" rather than the entire postal address
  if (parts.length >= 2 && parts[0].length <= 10) {
    return `${parts[0]}, ${parts[1]}`;
  }
  return parts[0] || full;
}

/** Hydrate input state from checkout/edit payload (title for field, full for API). */
export function placeFromStored({
  title,
  address,
  fullAddress,
  lat,
  lng,
} = {}) {
  const full = String(fullAddress || address || "").trim();
  const short = String(title || "").trim() || getPlaceDisplayTitle({
    address: full,
    fullAddress: full,
  }) || full;
  return {
    address: short,
    fullAddress: full || short,
    lat: lat ?? null,
    lng: lng ?? null,
  };
}

/** Map place state → API/stop payload with both title + full address. */
export function placeToStored(place) {
  const full = getPlaceFullAddress(place);
  const title = getPlaceDisplayTitle(place) || full;
  return {
    address: full,
    title,
    lat: place?.lat ?? null,
    lng: place?.lng ?? null,
  };
}

const useGooglePlaces = () => {
  const [suggestions, setSuggestions] = useState([]);

  const fetchSuggestions = useCallback(async (text) => {
    if (!text || text.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      await loadGoogleMaps();
    } catch (error) {
      console.error("Google Maps API not loaded", error);
      return;
    }

    if (!window.google?.maps?.places) {
      console.error("Google Maps Places library not available");
      return;
    }

    const autocompleteService =
      new window.google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions(
      { input: text, componentRestrictions: { country: "in" } },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          setSuggestions(predictions || []);
        } else {
          setSuggestions([]);
        }
      },
    );
  }, []);

  const getPlaceDetails = useCallback(async (placeId, prediction = null) => {
    try {
      await loadGoogleMaps();
    } catch (error) {
      throw new Error("Google Maps API not loaded");
    }

    if (!window.google?.maps?.places) {
      throw new Error("Google Maps Places library not available");
    }

    return new Promise((resolve, reject) => {
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );
      placesService.getDetails({ placeId }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const fullAddress =
            place.formatted_address ||
            prediction?.description ||
            place.name ||
            "";
          const mainText = getPlaceDisplayTitle(prediction) ||
            getPlaceDisplayTitle(place) ||
            fullAddress;

          resolve({
            // Short Uber-style title shown in the input field
            address: mainText,
            fullAddress,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        } else {
          reject(status);
        }
      });
    });
  }, []);

  return {
    suggestions,
    setSuggestions,
    fetchSuggestions,
    getPlaceDetails,
  };
};

export default useGooglePlaces;
